import { StoredSection } from '@/entities/templates';

import {
  DropSide,
  OPTION_DROP_ID_SEPARATOR,
  OptionTarget,
  PALETTE_DRAG_TYPES,
  PaletteDrop,
  PaletteDragType,
} from '../config';

import { getStrictInsertIndex, isAiOption } from './aiOption';
import { getSectionIds } from './getSectionIds';

type BuildOptionDropIdArgs = {
  sectionId: string;
  optionIndex: number;
};

export const buildOptionDropId = ({
  sectionId,
  optionIndex,
}: BuildOptionDropIdArgs): string =>
  `${sectionId}${OPTION_DROP_ID_SEPARATOR}${optionIndex}`;

type GetOptionTargetFromDropIdArgs = {
  sections: StoredSection[];
  optionId: string;
};

export const getOptionTargetFromDropId = ({
  sections,
  optionId,
}: GetOptionTargetFromDropIdArgs): OptionTarget | null => {
  const sectionIds = getSectionIds(sections);
  const sectionId = sectionIds.find((id) =>
    optionId.startsWith(`${id}${OPTION_DROP_ID_SEPARATOR}`),
  );

  if (!sectionId) {
    return null;
  }

  const optionIndex = parseInt(
    optionId.slice(sectionId.length + OPTION_DROP_ID_SEPARATOR.length),
    10,
  );
  const sectionIndex = sectionIds.indexOf(sectionId);

  if (
    sectionIndex === -1 ||
    Number.isNaN(optionIndex) ||
    !sections[sectionIndex]?.options?.[optionIndex]
  ) {
    return null;
  }

  return {
    sectionIndex,
    optionIndex,
  };
};

type ResolvePaletteDropParams = {
  dragType: PaletteDragType;
  overId: string;
  side: DropSide;
  sections: StoredSection[];
};

export const resolvePaletteDrop = ({
  dragType,
  overId,
  side,
  sections,
}: ResolvePaletteDropParams): PaletteDrop | null => {
  const overSectionIndex = getSectionIds(sections).indexOf(overId);

  if (dragType === PALETTE_DRAG_TYPES.SECTION) {
    if (overSectionIndex === -1) {
      return { kind: 'section', insertIndex: sections.length };
    }

    return {
      kind: 'section',
      insertIndex: side === 'before' ? overSectionIndex : overSectionIndex + 1,
    };
  }

  const optionTarget = getOptionTargetFromDropId({
    sections,
    optionId: overId,
  });

  if (dragType === PALETTE_DRAG_TYPES.FIELD) {
    if (!optionTarget) {
      return null;
    }

    const { sectionIndex, optionIndex } = optionTarget;
    const option = sections[sectionIndex].options?.[optionIndex];

    return option && !isAiOption(option)
      ? { kind: 'field', sectionIndex, optionIndex }
      : null;
  }

  if (optionTarget) {
    const { sectionIndex, optionIndex } = optionTarget;
    const options = sections[sectionIndex].options ?? [];
    const insertAt = side === 'before' ? optionIndex : optionIndex + 1;

    return {
      kind: 'option',
      sectionIndex,
      insertIndex: getStrictInsertIndex({ options, insertAt }),
    };
  }

  if (overSectionIndex === -1) {
    return null;
  }

  const options = sections[overSectionIndex].options ?? [];

  return {
    kind: 'option',
    sectionIndex: overSectionIndex,
    insertIndex: getStrictInsertIndex({ options, insertAt: options.length }),
  };
};
